import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import MotorMarkdownWrapper from '@adv-mt/mt-markdown-wrapper/lib/AdDetailsTc'
import SectionBasic from '@adv-mt/mt-section-basic'

const SectionFinancingLegalTerms = ({financingData = {}, titleSize, isBalloonInstalmentVariation}) => {
  const {domain, i18n} = useContext(Context)

  const config = domain.get('config')
  const {FINANCING_PARTNERS, LENDERS_LEGAL_CONDITIONS} = config.get('financing')
  const {entry, lenderLegalTextId, minEntry, terms} = financingData

  const [calculatorParams, setCalculatorParams] = useState({
    lenderLegalTextId: lenderLegalTextId || '',
    openingFee: entry || minEntry,
    terms
  })

  const isSantanderPri = financingData.lender === FINANCING_PARTNERS.SANTANDER_PRI
  const isFdaCalculatorB2CAvailable = financingData && financingData.loanRequestAvailable

  const _getNumericValue = value => {
    // format number to locale DE, because it separates thousands of 4 digits with dot
    if (isNaN(value)) {
      return ''
    }
    return value < 0
      ? ''
      : Intl.NumberFormat('de-DE', {
          minimumFractionDigits: 2
        }).format(value)
  }

  const _getCommissionTypeLiteral = ({isOpeningExpensesFinanced}) => {
    return isOpeningExpensesFinanced ? i18n.t('COMMON.FINANCED_FEM').toLowerCase() : i18n.t('COMMON.CASH').toLowerCase()
  }

  const financingInfo =
    isFdaCalculatorB2CAvailable && !financingData.hasCalculatedFinancingData && financingData?.calculatorB2cData
      ? financingData?.calculatorB2cData?.withoutInsurance
      : financingData

  const markdownParser = html => {
    const {
      casualtyInsuranceAmount,
      dateFirstInstallment,
      dateOfferValidUntil,
      dateSignup,
      firstFee,
      fee,
      insuranceRate,
      isOpeningExpensesFinanced,
      lifeInsuranceAmount,
      openingExpenses,
      openingPercentage,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms,
      dateOfferEnd
    } = financingInfo || {}

    const currentFinancedPrice = financingData.computedPrice || financingData?.financedPrice
    const replacements = {
      '%{amountToFinance}': _getNumericValue(financingData?.amountToFinance),
      '%{cashPrice}': _getNumericValue(financingData?.cashPrice),
      '%{casualtyInsuranceAmount}': _getNumericValue(casualtyInsuranceAmount),
      '%{commissionType}': _getCommissionTypeLiteral({
        isOpeningExpensesFinanced
      }),
      '%{contractDate}': dateSignup || financingData.dateSignup,
      '%{fee}': _getNumericValue(fee),
      '%{financedPrice}': _getNumericValue(currentFinancedPrice || financingData?.cashPrice),
      '%{firstFee}': firstFee > 0 ? _getNumericValue(firstFee) : '',
      '%{firstInstallmentDate}': dateFirstInstallment,
      '%{lifeInsuranceAmount}': _getNumericValue(lifeInsuranceAmount),
      '%{insuranceRate}': _getNumericValue(insuranceRate),
      '%{nTerms}': terms - 1,
      '%{offerValidUntilDate}': dateOfferValidUntil,
      '%{openingExpenses}': _getNumericValue(openingExpenses),
      '%{openingFee}': _getNumericValue(calculatorParams.openingFee || minEntry),
      '%{openingPercentage}': _getNumericValue(openingPercentage),
      '%{tae}': _getNumericValue(tae),
      '%{terms}': terms,
      '%{tin}': _getNumericValue(tin),
      '%{totalAmountDue}': _getNumericValue(totalAmountDue),
      '%{totalInsuranceAmount}': _getNumericValue(totalInsuranceAmount),
      '%{totalInterest}': _getNumericValue(totalInterest),
      '%{totalLoanAmount}': _getNumericValue(totalLoanAmount),
      '%{totalLoanAmountCost}': _getNumericValue(totalLoanCost),
      '%{totalLoanCost}': _getNumericValue(totalLoanCost),
      '%{totalPriceTerms}': _getNumericValue(totalPriceTerms),
      '%{offerEndDate}': dateOfferEnd
    }

    return html.replace(/%{\w+}/g, match => {
      return replacements[match] === undefined ? '' : replacements[match]
    })
  }

  const hasLenderLegalConditions = LENDERS_LEGAL_CONDITIONS.includes(financingData.lender)

  const title = {
    [FINANCING_PARTNERS.BBVA]: 'COMMON.FINANCING_TC_BBVA',
    [FINANCING_PARTNERS.BMW_BANK]: 'COMMON.FINANCING_TC_BMW_BANK',
    [FINANCING_PARTNERS.CAIXA]: 'COMMON.FINANCING_TC_CAIXA',
    [FINANCING_PARTNERS.LENDROCK]: 'COMMON.FINANCING_TC_LENDROCK',
    [FINANCING_PARTNERS.MOBILIZE]: 'COMMON.FINANCING_TC_MOBILIZE',
    [FINANCING_PARTNERS.MOBILIZE_NISSAN]: 'COMMON.FINANCING_TC_MOBILIZE_NISSAN',
    [FINANCING_PARTNERS.MOBILIZE_NISSAN_NSFS]: 'COMMON.FINANCING_TC_MOBILIZE_NISSAN',
    [FINANCING_PARTNERS.SANTANDER]: 'COMMON.FINANCING_TC_SANTANDER',
    [FINANCING_PARTNERS.SANTANDER_PRI]: 'COMMON.FINANCING_TC_SANTANDER',
    [FINANCING_PARTNERS.STELLANTIS]: 'COMMON.FINANCING_TC_STELLANTIS',
    [FINANCING_PARTNERS.VWFS]: 'COMMON.FINANCING_TC_VWFS',
    [FINANCING_PARTNERS.CONFIA]: 'COMMON.FINANCING_TC_CONFIA'
  }

  const lenderLegalConditionsTitle = title[financingData.lender] || ''
  const hasBalloonInstalment = isBalloonInstalmentVariation && Boolean(financingData.balloonFee)
  const literalSectionTitle = isSantanderPri ? 'COMMON.TERMS_CONDITIONS_LEGAL_PERSONAL_LOAN' : 'COMMON.FINANCING_TC'

  useEffect(() => {
    ;(async () => {
      setCalculatorParams({
        lenderLegalTextId: financingInfo.lenderLegalTextId || '',
        openingFee: financingInfo.entry || financingInfo.minEntry,
        terms: financingInfo.terms
      })
    })()
  }, [financingInfo])

  return (
    <SectionBasic title={i18n.t(literalSectionTitle)} titleSize={titleSize}>
      <div className="mt-SectionFinancingLegalTerms">
        <p className="mt-SectionFinancingLegalTerms-title">{i18n.t('COMMON.FINANCING_TC_ADEVINTA')}</p>
        <MotorMarkdownWrapper documentName="adevinta" />
        {hasBalloonInstalment && (
          <p className="mt-SectionFinancingLegalTerms-title">Consulte condiciones de financiación</p>
        )}
        {!hasBalloonInstalment && hasLenderLegalConditions ? (
          <>
            <p className="mt-SectionFinancingLegalTerms-title">{i18n.t(lenderLegalConditionsTitle)}</p>
            <MotorMarkdownWrapper
              key={`${financingInfo.lenderLegalTextId}-${financingInfo.openingFee}-${financingInfo.terms}`}
              documentName={financingInfo.lenderLegalTextId}
              parser={markdownParser}
            />
          </>
        ) : (
          <></>
        )}
      </div>
    </SectionBasic>
  )
}

SectionFinancingLegalTerms.propTypes = {
  financingData: PropTypes.object,
  isBalloonInstalmentVariation: PropTypes.object,
  titleSize: PropTypes.string
}

export default SectionFinancingLegalTerms
